/**
 * ShareLink — the room's invite block. Owner: Phase-3 A3.3 (this slice).
 *
 * Renders the room code in large monospaced text, a "Copy link" button (writes
 * `${location.origin}/?code=CODE` to clipboard with a graceful textarea
 * fallback for browsers that lack the async Clipboard API), and a small
 * visual "scan card" rendered as an inline SVG.
 *
 * QR fallback (no new deps, <100 LOC):
 *   We are explicitly NOT shipping a Reed-Solomon-encoded QR — that would need
 *   ~3kB of code per the QR Code spec, which is over budget. Instead we render
 *   an inline SVG "scan card" that shows the code in a high-contrast,
 *   phone-camera-readable serif at large size, with the share URL printed
 *   underneath. Anyone within line of sight can read the 6-character code and
 *   type it into the join screen, which is the same one-step flow they would
 *   do after scanning a real QR. Phase 4 (A3.4 multimodal Codex) may replace
 *   this with a real QR if it does so without adding an npm dep.
 *
 * Props:
 *   code — the room code (already uppercased upstream).
 *
 * The Copy button has three states: idle, "copied!" (transient), and
 * "copy failed" (transient). All three are accessible via aria-live so a
 * screen reader hears the outcome.
 */
import { useCallback, useMemo, useState } from "react";

const COPIED_RESET_MS = 1_800;

function buildShareUrl(code: string): string {
  if (typeof window === "undefined") return `/?code=${code}`;
  const origin = window.location?.origin ?? "";
  return `${origin}/?code=${encodeURIComponent(code)}`;
}

/** Best-effort clipboard write. Falls back to a hidden textarea + execCommand
 * for older browsers / iOS Safari < 13.4 / non-secure contexts where the
 * async Clipboard API is unavailable. Returns true on success. */
async function copyToClipboard(text: string): Promise<boolean> {
  try {
    if (
      typeof navigator !== "undefined" &&
      navigator.clipboard &&
      typeof navigator.clipboard.writeText === "function"
    ) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    /* fall through to legacy path */
  }
  try {
    if (typeof document === "undefined") return false;
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.setAttribute("readonly", "");
    ta.style.position = "fixed";
    ta.style.top = "-1000px";
    ta.style.left = "-1000px";
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}

export interface ShareLinkProps {
  code: string;
}

export function ShareLink({ code }: ShareLinkProps): JSX.Element {
  const url = useMemo(() => buildShareUrl(code), [code]);
  const [status, setStatus] = useState<"idle" | "copied" | "failed">("idle");

  const onCopy = useCallback(() => {
    void copyToClipboard(url).then((ok) => {
      setStatus(ok ? "copied" : "failed");
      setTimeout(() => setStatus("idle"), COPIED_RESET_MS);
    });
  }, [url]);

  const label =
    status === "copied" ? "Copied!" : status === "failed" ? "Copy failed" : "Copy link";

  // Phase-7 status → utility-class palette. Idle is the base copy button
  // (no extra utilities); copied/failed add a green/red tinted background +
  // border via arbitrary colors (the prior CSS used the same RGB tuples).
  const statusClass =
    status === "copied"
      ? "border-[rgb(76_209_129/0.6)] bg-[rgb(76_209_129/0.25)]"
      : status === "failed"
        ? "border-[rgb(198_59_59/0.6)] bg-[rgb(198_59_59/0.25)]"
        : "";

  return (
    <section
      className="flex flex-col gap-2.5 rounded-md border border-white/10 bg-white/[0.04] px-3.5 py-3"
      aria-label="Room invite"
    >
      <div className="flex flex-col gap-0.5">
        <span className="text-[11px] uppercase tracking-[1.5px] opacity-70">
          Room code
        </span>
        <code
          className="select-all font-mono text-[32px] font-bold tracking-[6px] text-gold-400"
          aria-label={`Room code ${code.split("").join(" ")}`}
        >
          {code}
        </code>
      </div>
      <div className="flex flex-wrap items-center gap-2.5">
        <button
          type="button"
          className={`min-h-11 px-3.5 py-1.5 text-[13px] ${statusClass}`}
          onClick={onCopy}
          aria-live="polite"
        >
          {label}
        </button>
        <a
          className="cursor-default font-mono text-xs text-ink-100/65 underline decoration-dotted underline-offset-[3px] [overflow-wrap:anywhere]"
          href={url}
          onClick={(e) => e.preventDefault()}
          title="Share this link with friends"
        >
          {url}
        </a>
      </div>
      <ScanCard code={code} url={url} />
    </section>
  );
}

/** Inline-SVG visual "scan card". Pure presentation; no dependency. */
function ScanCard({ code, url }: { code: string; url: string }): JSX.Element {
  return (
    <svg
      className="h-[120px] w-[200px] rounded-lg shadow-card"
      viewBox="0 0 200 120"
      role="img"
      aria-label={`Scan card for room ${code}`}
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect x="0" y="0" width="200" height="120" rx="10" ry="10" fill="#f4f1e6" />
      <rect x="6" y="6" width="188" height="108" rx="6" ry="6" fill="none" stroke="#0c1410" strokeWidth="1.5" />
      <text
        x="100"
        y="58"
        textAnchor="middle"
        fontFamily="ui-monospace, SFMono-Regular, Menlo, monospace"
        fontSize="34"
        fontWeight="700"
        fill="#0c1410"
        letterSpacing="3"
      >
        {code}
      </text>
      <text
        x="100"
        y="92"
        textAnchor="middle"
        fontFamily="ui-sans-serif, system-ui, sans-serif"
        fontSize="9"
        fill="#1a2622"
      >
        {url.length > 36 ? url.slice(0, 33) + "..." : url}
      </text>
    </svg>
  );
}
